import { VStack, Span, HStack, Badge } from "@chakra-ui/react"
import SearchCombobox, { UseSearchQueryHook } from "./SearchCombobox"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import Link from "@hydrologiq/shared/components/Link"

export type UnifiedSiteItem = {
  id: string
  name: string
  location?: {
    id: string
    name: string
    lat: number
    long: number
  }
  types: string[] // ["customer", "filling"]
  customers?: Array<{
    id: string
    name: string
  }>
  is_customer_site: boolean
  is_filling_site: boolean
  filling_site_id?: string // ID of the filling_site record if this is a fill site
}

export interface SitesComboboxProps {
  onItemSelection?: (value: UnifiedSiteItem | undefined) => void
  initialValue?: UnifiedSiteItem
  value?: UnifiedSiteItem | null // Controlled value (overrides initialValue if provided)
  valueId?: string | null // Controlled value by ID only (used when full object not available yet)
  siteTypes?: string[] // Filter to specific types: ["customer"], ["filling"], or both
  placeholder?: string
  disabled?: boolean
  fieldLabel?: string // Custom field label (defaults to "Site" or based on siteTypes)
  autoSelectFirst?: boolean // Auto-select first item when items load
  refreshTrigger?: number // Trigger to refresh data (increment to trigger refresh)
  required?: boolean // Whether the field is required
  // Hook to use for search queries (must be provided by consumer)
  useSearchQuery: UseSearchQueryHook<UnifiedSiteItem>
}

const SitesCombobox = (props: SitesComboboxProps) => {
  const {
    onItemSelection,
    initialValue,
    value,
    valueId,
    siteTypes,
    placeholder,
    disabled,
    fieldLabel,
    autoSelectFirst,
    refreshTrigger,
    required,
    useSearchQuery,
  } = props

  // Determine field label based on filter
  const getFieldLabel = () => {
    if (fieldLabel) return fieldLabel
    if (!siteTypes || siteTypes.length === 0) return "sites"
    if (siteTypes.length === 1 && siteTypes[0] === "filling") return "fill sites"
    return "sites"
  }

  // Determine create link based on filter
  const getCreateLink = () => {
    if (siteTypes && siteTypes.length === 1 && siteTypes[0] === "filling") return "/filling"
    if ((siteTypes && "customer" in siteTypes) || !siteTypes) return "/customers"
    return undefined
  }
  const link = getCreateLink()

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.CUSTOMER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CUSTOMER],
    [COPY_KEYS.HEADINGS.FILL_SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.FILL_SITE],
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
  })

  return (
    <SearchCombobox<UnifiedSiteItem>
      queryType="sites"
      onItemSelection={onItemSelection}
      placeholder={placeholder}
      label={getFieldLabel()}
      formatSelectedItem={(item) => item.name}
      initialValue={initialValue}
      value={value}
      valueId={valueId}
      disabled={disabled}
      additionalVariables={{ types: siteTypes }}
      autoSelectFirst={autoSelectFirst}
      refreshTrigger={refreshTrigger}
      required={required}
      useSearchQuery={useSearchQuery}
      customFilter={(item, searchTerm) => {
        // Search in site name
        const matchesName = item.name.toLowerCase().includes(searchTerm)

        // Search in customer names if available
        const matchesCustomer =
          item.customers?.some((customer) => customer.name.toLowerCase().includes(searchTerm)) || false

        return matchesName || matchesCustomer
      }}
      itemContent={(item) => (
        <VStack gap={1} align="stretch" width="100%">
          <Span width="100%" textStyle="sm" fontWeight="medium">
            {item.name}
          </Span>

          {/* Type badges */}
          {item.types && item.types.length > 0 && (
            <HStack gap={1}>
              {item.is_customer_site && (
                <Badge size="sm" colorPalette="orange">
                  {copy[COPY_KEYS.HEADINGS.CUSTOMER]}
                </Badge>
              )}
              {item.is_filling_site && (
                <Badge size="sm" colorPalette="green">
                  {copy[COPY_KEYS.HEADINGS.FILL_SITE]}
                </Badge>
              )}
            </HStack>
          )}

          {/* Customer names if applicable */}
          {item.customers && item.customers.length > 0 && (
            <Span width="100%" textStyle="xs" color="fg.muted">
              {item.customers.length === 1
                ? `Customer: ${item.customers[0].name}`
                : `Customers: ${item.customers.map((c) => c.name).join(", ")}`}
            </Span>
          )}
        </VStack>
      )}
      CreateAction={
        link
          ? () => (
              <Link href={link} variant="primary">
                Create {copy[COPY_KEYS.HEADINGS.SITE].toLowerCase()}
              </Link>
            )
          : undefined
      }
    />
  )
}

export default SitesCombobox
